import React, { useEffect, useRef, useState } from "react";
import "./messenger.css";
import {
  CloudUpload,
  EllipsisVertical,
  SendHorizontal,
  Smile,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  arrayUnion,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
} from "firebase/storage";
import { serverTimestamp } from "firebase/firestore";
import { firestore, storage } from "../../firebase";
import { useDispatch, useSelector } from "react-redux";
import toastr from "toastr";
import {
  handleCurrentUserDate,
  handleCurrentUserMessages,
  handleUnreadChatNotifications,
  setUnreadMessagesCount,
  updateChatMembers,
  updateNewChat,
  updateSortChat,
} from "../../redux/chatData";
import { getColorFromName } from "../../utils/getColor";
import AllChatMembersModal from "./AllChatMembersModal";
import apiServiceHandler from "../../service/apiService";
import ReactionsModal from "./ReactionsModal";

const isValidUrl = (urlString) => {
  const urlRegex = /^(https?:\/\/|http:\/\/|www\.)[^\s]+$/i;
  return urlRegex.test(urlString);
};

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const groupMessagesByDate = (messages) => {
  if (messages.length === 0) return ["", []];

  const data = messages?.reduce((groups, message) => {
    const date = message.createdAt
      ? formatDate(message.createdAt.toDate())
      : "Unknown Date";
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return data;
};

const Messenger = ({ activeChat }) => {
  // console.log(activeChat)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [allMembersModal, setAllMembersModal] = useState(false);
  const [chatMembers, setChatMembers] = useState([]);
  const dispatch = useDispatch();
  const [textMessage, setTextMessage] = useState("");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const currentUserMessages = useSelector(
    (state) => state.chatData.currentUserMessages
  );
  const oldChats = useSelector((state) => state.chatData.oldChats);
  const newChatMembers = useSelector((state) => state.chatData.newChatMembers);
  const user = useSelector((state) => state.userData.userDetails);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedMessageText, setEditedMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState(false);
  const userName = useSelector((state) => state.userData.userDetails.name);

  const [openReactionsModal, setOpenReactionsModal] = useState(false)
  const [currentMessage, setCurrentMessage] = useState(null)

  const toggleReactionsModal = (msg) => {
    setOpenReactionsModal(!openReactionsModal)
    if (msg) {
      setCurrentMessage(msg)
    }
  }

  useEffect(() => {
    if (activeChat) {
      const threadHistory = document.querySelector(".thread-history");

      if (threadHistory) {
        const infiniteScrollContainer =
          threadHistory.querySelector(".infinite-scroll");

        if (infiniteScrollContainer) {
          const lastMessage = infiniteScrollContainer.lastElementChild;

          if (lastMessage) {
            // Function to scroll to the last message
            const scrollToBottom = () => {
              lastMessage.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            };

            // Find media elements (images & videos)
            const mediaElements = infiniteScrollContainer.querySelectorAll(
              "img, video"
            );

            let mediaLoaded = 0;
            const totalMedia = mediaElements.length;

            if (totalMedia === 0) {
              // No media elements, scroll immediately
              scrollToBottom();
            } else {
              mediaElements.forEach((media) => {
                if (media.complete || (media.readyState && media.readyState >= 3)) {
                  // Media is already loaded
                  mediaLoaded++;
                  if (mediaLoaded === totalMedia) {
                    scrollToBottom();
                  }
                } else {
                  // Media is still loading, attach load/error listeners
                  media.addEventListener("load", () => {
                    mediaLoaded++;
                    if (mediaLoaded === totalMedia) {
                      scrollToBottom();
                    }
                  });

                  media.addEventListener("error", () => {
                    mediaLoaded++;
                    if (mediaLoaded === totalMedia) {
                      scrollToBottom();
                    }
                  });
                }
              });
            }
          }
        }
      }
    }
  }, [activeChat, currentUserMessages]); 
  

  const toggleAllMembersModal = () => {
    setAllMembersModal(!allMembersModal);
  };

  const getAllChatMembers = async () => {
    try {
      // console.log(activeChat, 'what is this active chat value we have??')
      const response = await apiServiceHandler(
        "GET",
        `api/chat/members/${activeChat._id}`,
        {}
      );
      setChatMembers(response.data);
    } catch (error) {
      console.log("Error fetching members", error);
    }
  };

  useEffect(() => {
    if (activeChat) {
      getAllChatMembers();
    }
  }, [activeChat]);

  useEffect(() => {
    if (newChatMembers) {
      getAllChatMembers();
    }

    dispatch(updateChatMembers(false));
  }, [newChatMembers]);

  const fileInputRef = useRef(null);

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    console.log(file, "filetype");
    if (file) {
      sendMessage(file);
    }
  };

  const handleDoubleClick = (msg) => {
    setIsEditing(true);
    setEditingMessageId(msg.id);
    setEditedMessageText(msg.textMessage);
    setHoveredMessageId(null);
    setEditPopover(false);
  };

  const handleSaveEditedMessage = async () => {
    const messageRef = doc(
      firestore,
      `group/${activeChat?._id}/messages/${editingMessageId}`
    );

    await updateDoc(messageRef, {
      textMessage: editedMessageText,
    });

    // Reset the state
    setIsEditing(false);
    setEditingMessageId(null);
    setEditedMessageText("");
  };

  const closeEditMode = () => {
    setIsEditing(false);
    setEditingMessageId(null);
    setEditedMessageText("");
  };

  const onEmojiReactionClick = async (emojiData, messageId) => {
    setSelectedEmoji(true);
    const messageRef = doc(
      firestore,
      `group/${activeChat?._id}/messages/${messageId}`
    );

    const messageSnapshot = await getDoc(messageRef);
    const currentReactions = messageSnapshot.exists()
      ? messageSnapshot.data().reactions || []
      : [];

    const existingReactionIndex = currentReactions.findIndex(
      (reaction) => reaction.userId === userDetails?._id
    );

    let updatedReactions;

    if (existingReactionIndex !== -1) {
      // User has already reacted, update their emoji
      updatedReactions = currentReactions.map((reaction) =>
        reaction.userId === userDetails?._id
          ? { emoji: emojiData.emoji, userId: userDetails?._id, name: userName }
          : reaction
      );
    } else {
      updatedReactions = [
        ...currentReactions,
        { emoji: emojiData.emoji, userId: userDetails?._id, name: userName },
      ];
    }
    setHoveredMessageId(null);

    await updateDoc(messageRef, {
      reactions: updatedReactions,
    });
  };
  const sendPushNotification = async() => {
    try {

      const response = await apiServiceHandler(
        "POST",
        `api/chat/send/notification`,
        {groupMessageId: activeChat._id}
      );
      console.log(response, 'Response value is here-->>')
    }
    catch( error) {
      console.log(error, 'Error:-->>>>')
    }
  }
  const sendMessage = async (mediaFile = null) => {
    if (isSending) return;
    setIsSending(true);
    try {
      const members = [];
      activeChat?.members?.map((member) => {
        if (activeChat?.owner === member) {
          members.push({ id: member, seen: true });
        } else {
          members.push({ id: member, seen: false });
        }
      });

      if (textMessage.trim() !== "" || mediaFile) {
        const docRef = doc(firestore, "group", activeChat?._id);
        const messagesRef = collection(docRef, "messages");

        let mediaUrl = null;
        if (mediaFile) {
          const mediaRef = storageRef(
            storage,
            `group/${activeChat?._id}/${mediaFile.name}`
          );

          const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/jpg",
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ];
          const maxSize = 10 * 1024 * 1024;
          if (!allowedTypes.includes(mediaFile.type)) {
            alert("Unsupported file type!");
            setIsSending(false);
            return;
          }
          if (mediaFile.size > maxSize) {
            alert("File size exceeds the 10MB limit!");
            setIsSending(false);
            return;
          }

          let isToastrShown = false;
          // Upload file with progress tracking
          const uploadTask = uploadBytesResumable(mediaRef, mediaFile);
          await new Promise((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (!isToastrShown) {
                  toastr.info("Uploading file");
                  isToastrShown = true;
                }
              },
              (error) => {
                toastr.error("Upload Failed");
                reject(error);
                setIsSending(false);
              },
              async () => {
                mediaUrl = await getDownloadURL(uploadTask.snapshot.ref);
                resolve();
                toastr.success("Upload Success");
                setIsSending(false);
              }
            );
          });
        }

        const body = {
          textMessage,
          mediaUrl,
          mediaType: mediaFile ? mediaFile.type : "text",
          fileName: mediaFile ? mediaFile.name : "",
          createdAt: serverTimestamp(),
          uid: userDetails?._id,
          displayName: user?.name,
          image: userDetails?.image || "",
          lastSeen: members,
          group: activeChat?._id,
        };

        // console.log(body, 'body....')

        await addDoc(messagesRef, body);
        setTextMessage("");
        const groupRef = doc(firestore, "group", activeChat?._id);

        await setDoc(
          groupRef,
          {
            lastMessageTimestamp: serverTimestamp(),
          },
          { merge: true }
        );

        for (const userId of activeChat?.members) {
          const userRef = doc(firestore, "notification", userId);

          // Fetch the current notifications document to check if the notification exists
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            const notifications = data.notifications || [];

            // Check if the notification with the same chatId already exists
            const existingNotification = notifications.find(
              (notification) => notification.chatId === activeChat?._id
            );

            if (existingNotification) {
              // If the notification exists, update the timestamp and read status
              const updatedNotifications = notifications.map((notification) =>
                notification.chatId === activeChat?._id
                  ? {
                      ...notification,
                      timestamp: Date.now(),
                      read: userDetails?._id !== userId ? false : true,
                    } // Update the existing notification
                  : notification
              );

              // Update the notifications array in the Firestore document
              await updateDoc(userRef, {
                notifications: updatedNotifications,
              });

              console.log("Notification updated successfully.");
            } else {
              // If the notification doesn't exist, add a new one using arrayUnion
              await setDoc(
                userRef,
                {
                  notifications: arrayUnion({
                    chatId: activeChat?._id,
                    timestamp: Date.now(),
                    read: userDetails?._id !== userId ? false : true,
                  }),
                },
                { merge: true }
              );

              console.log("New notification added.");
            }
          } else {
            // If no notifications document exists, create one with the new notification
            await setDoc(
              userRef,
              {
                notifications: [
                  {
                    chatId: activeChat?._id,
                    timestamp: Date.now(),
                    read: userDetails?._id !== userId ? false : true,
                  },
                ],
              },
              { merge: true }
            );

            console.log("Notification document created with new notification.");
          }
        }
        // setMediaFile(null);
        dispatch(updateSortChat(true));
        await sendPushNotification()
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    const unsubscribes = [];
    const fetchDocuments = () => {
      let chatRefId = `group/${activeChat?._id}/messages`;
      const q = query(collection(firestore, chatRefId), orderBy("createdAt"));

      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        const messages = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (activeChat) {
          const groupedMessages = groupMessagesByDate(messages);
          const groupedMessageDates = Object.keys(groupedMessages);
          if (groupedMessageDates?.length > 0) {
            groupedMessageDates?.forEach((firstDate) => {
              dispatch(handleCurrentUserDate(firstDate));
              dispatch(handleCurrentUserMessages(groupedMessages));
            });
          }
        }
      });
      unsubscribes.push(unsubscribe);
    };

    if (oldChats > 0) {
      fetchDocuments();
    }

    return () => {
      unsubscribes?.forEach((unsubscribe) => unsubscribe());
    };
  }, [activeChat]);

  const updateReadStatus = async () => {
    const userId = activeChat?.members.find((id) => id === userDetails?._id);
    if (!userId) return;

    const userRef = doc(firestore, "notification", userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      const notifications = data.notifications || [];

      const existingNotification = notifications.find(
        (notification) => notification.chatId === activeChat?._id
      );

      if (existingNotification && !existingNotification.read) {
        const updatedNotifications = notifications.map((notification) =>
          notification.chatId === activeChat?._id
            ? { ...notification, read: true }
            : notification
        );

        dispatch(handleUnreadChatNotifications(updatedNotifications));

        await updateDoc(userRef, { notifications: updatedNotifications });
      }
    }
  };

  useEffect(() => {
    if (activeChat) {
      updateReadStatus();
    }
  }, [activeChat]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendMessage();
    }
  };

  const displayActions = (id) => {
    setHoveredMessageId(id);
  };

  const [editPopover, setEditPopover] = useState(false);

  return (
    <>
      {activeChat ? (
        <div className="messenger">
          <div className="messenger__header">
            <div className="messenger__thread-info">
              <div className="messenger-truncate">{activeChat?.name}</div>
              <div className="thread-count" onClick={toggleAllMembersModal}>
                {chatMembers && chatMembers?.length + " Members"}
              </div>
            </div>
          </div>
          <div className="thread-history">
            {activeChat &&
              !(
                currentUserMessages.length == 2 && currentUserMessages[0] == ""
              ) && (
                <div className="no-more-results">
                  This is the start of this conversation
                </div>
              )}

            <div className="infinite-scroll">
              {currentUserMessages.length == 2 &&
              currentUserMessages[0] == "" ? (
                <span style={{ textAlign: "center", marginTop: "1rem" }}>
                  No Messages
                </span>
              ) : (
                currentUserMessages &&
                Object.keys(currentUserMessages)?.map((date, index) => (
                  <>
                    <span className="timeDateFormat">
                      <span>{date}</span>
                    </span>
                    {currentUserMessages[date] &&
                      currentUserMessages[date]?.map((msg, index) => (
                        <>
                          {msg?.uid === userDetails?._id && (
                            <div
                              className="thread-message sent"
                              onMouseLeave={() => {
                                setHoveredMessageId(null);
                                setShowEmojiPicker(false);
                                setEditPopover(false);
                              }}
                            >
                              <div className="thread-message__container">
                                <div
                                  className={`message message-base text start ${
                                    isEditing && editingMessageId === msg.id
                                      ? "edit-input"
                                      : ""
                                  } `}
                                  onMouseEnter={() => displayActions(msg?.id)}
                                  style={
                                    msg?.mediaType.startsWith("image/")
                                      ? { background: "none", maxWidth: "40%" }
                                      : {}
                                  }
                                >
                                  {isEditing && editingMessageId === msg?.id ? (
                                    <div className="chat-text-input">
                                      <div className="chat-text-input__wrapper">
                                        <input
                                          placeholder="your message..."
                                          value={editedMessageText}
                                          onChange={(e) =>
                                            setEditedMessageText(e.target.value)
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      className="text is-styled"
                                      onDoubleClick={() =>
                                        handleDoubleClick(msg)
                                      }
                                      style={
                                        msg.mediaType.startsWith("image/")
                                          ? {
                                              display: "flex",
                                              justifyContent: "flex-end",
                                            }
                                          : {}
                                      }
                                    >
                                      {msg?.mediaType &&
                                        (msg.mediaType.startsWith("image/") ? (
                                          <img
                                            src={msg?.mediaUrl}
                                            alt="Media"
                                            style={{ width: "100%" }}
                                          />
                                        ) : msg.mediaType ===
                                            "application/pdf" ||
                                          msg.mediaType ===
                                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                                          <a
                                            href={msg?.mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: "#007bff",
                                              textDecoration: "underline",
                                            }}
                                          >
                                            {msg?.fileName}
                                          </a>
                                        ) : isValidUrl(msg?.textMessage) ? (
                                          <a
                                            href={msg?.textMessage}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: "#007bff",
                                              textDecoration: "underline",
                                            }}
                                          >
                                            {msg?.textMessage}
                                          </a>
                                        ) : (
                                          <span>{msg?.textMessage}</span>
                                        ))}
                                    </div>
                                  )}
                                </div>
                                {msg?.reactions?.length > 0 && (
                                  <div className="message-reaction">
                                    {msg?.reactions?.slice(0,2)?.map((reaction, index) => (
                                      <span
                                        key={index}
                                        className="emoji-reaction"
                                        style={{cursor: 'pointer'}}
                                        onClick={() => toggleReactionsModal(msg)}
                                      >
                                        {reaction.emoji}
                                      </span>
                                    ))}
                                    {msg?.reactions?.length > 2 && (
                                      <span className="emoji-reaction-count" onClick={() => toggleReactionsModal(msg)}>
                                        {msg.reactions.length - 2}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {!isEditing && hoveredMessageId === msg?.id && (
                                  <div className="message-menu">
                                    <div className="message-menu__item">
                                      {showEmojiPicker &&
                                        msg.id === hoveredMessageId && (
                                          <div className="reaction-sent-picker">
                                            <EmojiPicker
                                              onEmojiClick={(emojiData) =>
                                                onEmojiReactionClick(
                                                  emojiData,
                                                  msg?.id
                                                )
                                              }
                                              reactionsDefaultOpen={true}
                                            />
                                          </div>
                                        )}
                                      {msg?.mediaType === "text" && (
                                        <EllipsisVertical
                                          width={20}
                                          height={20}
                                          color="#d3d7da"
                                          onClick={() => {
                                            setEditPopover(!editPopover);
                                            setShowEmojiPicker(false);
                                          }}
                                        />
                                      )}
                                      {editPopover && (
                                        <div
                                          className="chat-dropdown-popover"
                                          style={{ inset: "none" }}
                                        >
                                          <div
                                            className="dropdown-content"
                                            style={{
                                              display: "flex",
                                              width: "4rem",
                                              padding: "0.7rem 0",
                                            }}
                                          >
                                            <div
                                              className="dropdown-item"
                                              style={{
                                                fontSize: "12px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginLeft: "0",
                                                gap: "0.4rem",
                                              }}
                                              onClick={() =>
                                                handleDoubleClick(msg)
                                              }
                                            >
                                              Edit
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="message-menu__item">
                                      <Smile
                                        width={20}
                                        height={20}
                                        color="#d3d7da"
                                        onClick={() => {
                                          setShowEmojiPicker(!showEmojiPicker);
                                          setEditPopover(false);
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                              {isEditing && editingMessageId === msg?.id && (
                                <div className="edit-btn-container">
                                  <button
                                    type="button"
                                    class="cancel-btn"
                                    onClick={closeEditMode}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    class="save-btn"
                                    onClick={handleSaveEditedMessage}
                                  >
                                    Save
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {msg?.uid !== userDetails?._id && (
                            <div
                              className="thread-message received"
                              onMouseLeave={() => {
                                setHoveredMessageId(null);
                                setShowEmojiPicker(false);
                                setEditPopover(false);
                              }}
                            >
                              <div className="sender">{msg?.displayName}</div>
                              <div className="thread-message__container">
                                <div
                                  className="chat-avatar"
                                  style={{
                                    backgroundColor: getColorFromName(
                                      msg?.displayName
                                    ),
                                  }}
                                >
                                  <div className="messenger-avatar__image">
                                    <div className="user__avatar">
                                      {msg?.displayName.substr(0, 1)}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className="message message-base text start"
                                  onMouseEnter={() => displayActions(msg?.id)}
                                  style={
                                    msg?.mediaType.startsWith("image/")
                                      ? { background: "none", maxWidth: "40%" }
                                      : {}
                                  }
                                >
                                  <div
                                    className="text is-styled"
                                    style={
                                      msg.mediaType.startsWith("image/")
                                        ? {
                                            display: "flex",
                                            justifyContent: "flex-start",
                                          }
                                        : {}
                                    }
                                  >
                                    {msg?.mediaType &&
                                      (msg.mediaType.startsWith("image/") ? (
                                        <img
                                          src={msg?.mediaUrl}
                                          alt="Media"
                                          style={{ width: "100%" }}
                                        />
                                      ) : msg.mediaType === "application/pdf" ||
                                        msg.mediaType ===
                                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                                        <a
                                          href={msg?.mediaUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#007bff",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {msg?.fileName}
                                        </a>
                                      ) : isValidUrl(msg?.textMessage) ? (
                                        <a
                                          href={msg?.textMessage}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#007bff",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {msg?.textMessage}
                                        </a>
                                      ) : (
                                        <span>{msg?.textMessage}</span>
                                      ))}
                                  </div>
                                  {msg?.reactions?.length > 0 && (
                                    <div className="message-received-reaction">
                                      {msg?.reactions
                                        ?.slice(0, 2)
                                        .map((reaction, index) => (
                                          <span
                                            key={index}
                                            className="emoji-reaction"
                                            style={{cursor: 'pointer'}}
                                            onClick={() => toggleReactionsModal(msg)}
                                          >
                                            {reaction.emoji}
                                          </span>
                                        ))}
                                      {msg?.reactions?.length > 2 && (
                                        <span className="emoji-reaction-count" onClick={() => toggleReactionsModal(msg)}>
                                          {msg.reactions.length - 2}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {!isEditing && hoveredMessageId === msg?.id && (
                                  <div className="received-message-menu">
                                    <div className="message-menu__item">
                                      {showEmojiPicker &&
                                        msg.id === hoveredMessageId && (
                                          <div className="reaction-received-picker">
                                            <EmojiPicker
                                              onEmojiClick={(emojiData) =>
                                                onEmojiReactionClick(
                                                  emojiData,
                                                  msg?.id
                                                )
                                              }
                                              reactionsDefaultOpen={true}
                                            />
                                          </div>
                                        )}
                                      <Smile
                                        width={20}
                                        height={20}
                                        color="#d3d7da"
                                        onClick={() => {
                                          setShowEmojiPicker(!showEmojiPicker);
                                          setEditPopover(false);
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ))}
                  </>
                ))
              )}
            </div>
          </div>
          {oldChats > 0 && (
            <div className="messenger__input">
              <div className="messenger-input-container">
                <div className="chat-text-input">
                  <div className="chat-text-input__input">
                    <fieldset className="input-wrapper__size-keeper">
                      <div className="input-wrapper__container">
                        <div className="input-wrapper__field">
                          <div className="input-wrapper__text">
                            <div className="textarea__resize-wrapper">
                              <textarea
                                placeholder="Your message..."
                                aria-invalid="false"
                                aria-errormessage=""
                                loading="false"
                                aria-label=""
                                className="textarea__textarea"
                                spellcheck="false"
                                style={{ paddingBottom: "1rem" }}
                                value={textMessage}
                                onKeyDown={handleKeyPress}
                                onChange={(e) => setTextMessage(e.target.value)}
                                rows={1}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
                <div className="chat-actions">
                  <div className="file-input">
                    <div
                      className="file-input-activator"
                      onClick={handleFileInputClick}
                    >
                      <input
                        ref={fileInputRef}
                        className="icon-attachment"
                        style={{ display: "none" }}
                        type="file"
                        onChange={handleFileChange}
                        disabled={isSending}
                        // onClick={sendMessage}
                      />
                      <CloudUpload
                        style={{
                          width: "20px",
                          height: "20px",
                          color: "#d1b794",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="no-chat">
          You don't have an active chat. Start a new conversation to begin
          chatting.
        </div>
      )}
      <AllChatMembersModal
        isOpen={allMembersModal}
        onClose={toggleAllMembersModal}
        chatMembers={chatMembers}
        chatId={activeChat?._id}
      />
      <ReactionsModal isOpen={openReactionsModal} onClose={toggleReactionsModal} currentMessage={currentMessage} activeChat={activeChat} />
    </>
  );
};

export default Messenger;
